import React, { useContext, useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { updateProfile } from "firebase/auth";
import { db, auth } from "./firebase";
import { useNavigate } from "react-router-dom";
import useUserData from "../hooks/useUserData";
import { SiteStateContext } from "./SiteStateContext";

const TeamDashboard = () => {
  const { userData, loading } = useUserData();
  const [teamData, setTeamData] = useState(null);
  const [authNameSynced, setAuthNameSynced] = useState(false);
  const navigate = useNavigate();
  const { impersonateTeamId, impersonateTeamName, isImpersonating } =
  useContext(SiteStateContext);
  const isAdmin = Boolean(userData?.adminUser);
  const isAdminImpersonating = isImpersonating && isAdmin;
  const effectiveTeamId =
  isAdminImpersonating ? impersonateTeamId : userData?.teamId;
  const effectiveTeamName =
  isAdminImpersonating ?
  impersonateTeamName || userData?.teamName :
  userData?.teamName;

  useEffect(() => {
    if (!loading) {
      if (!userData) {
        navigate("/starsonata");
      } else if (!isAdminImpersonating && userData.teamId === "unassigned") {
        navigate("/join");
      } else if (!isAdminImpersonating && !userData.verified) {
        navigate("/starsonata");
      }
    }
  }, [userData, loading, navigate, isAdminImpersonating]);

  useEffect(() => {
    const fetchTeamData = async () => {
      if (effectiveTeamId) {
        const teamRef = doc(db, "teams", effectiveTeamId);
        const snap = await getDoc(teamRef);

        if (snap.exists()) {
          const team = snap.data();
          const updatedTeam = { ...team };

          if (team.leaderUid) {
            updatedTeam.leaderName = team.leaderName || "Not Set";
          }

          setTeamData(updatedTeam);
        }
      }
    };

    fetchTeamData();
  }, [effectiveTeamId]);

  useEffect(() => {
    if (authNameSynced) return;
    const syncAuthDisplayName = async () => {
      const currentUser = auth.currentUser;
      const desiredName = userData?.username;
      if (!currentUser || !desiredName) return;
      if (currentUser.displayName === desiredName) {
        setAuthNameSynced(true);
        return;
      }
      try {
        await updateProfile(currentUser, { displayName: desiredName });
        await currentUser.reload();
      } catch (err) {
        console.error("Failed to sync auth displayName:", err);
      } finally {
        setAuthNameSynced(true);
      }
    };

    syncAuthDisplayName();
  }, [authNameSynced, userData?.username]);

  const handleCopyUserToken = () => {
    if (!userData?.userToken) return;
    navigator.clipboard
      .writeText(userData.userToken)
      .catch((err) => console.error("Failed to copy token:", err));
  };

  if (loading || !userData) return <p>Loading team info...</p>;

  return (
    <div style={styles.container}>

      <h1 style={{ color: "var(--theme-text-color)" }}>
        Welcome,{" "}
        <span className={userData?.adminUser ? "admin-user-name" : undefined}>
          {userData.username}
        </span>
        !
        </h1>
      <h2 style={{ color: "var(--theme-text-color)" }}>
       Team: <strong>{effectiveTeamName}</strong>
        </h2>
      {teamData?.leaderName ?
      <p style={{ color: "var(--theme-text-color)" }}>
          <strong>Team Leader:</strong> {teamData.leaderName}
        </p> :

      <p style={{ color: "var(--theme-text-color)" }}>Team Leader: Not Set</p>
      }
      <p style={{ color: "var(--theme-text-color)" }}>
        <strong>Your Login Token:</strong>{" "}
        <span
          role="button"
          tabIndex={0}
          title="Click to copy token."
          onClick={handleCopyUserToken}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleCopyUserToken();
            }
          }}
          style={{ cursor: userData?.userToken ? "pointer" : "default" }}>
          {userData?.userToken || "Not Set"}
        </span>
      </p>
    </div>);

};

const styles = {
  container: {
    padding: "2rem",
    textAlign: "center",
    backgroundColor: "var(--theme-color)",
    borderRadius: "10px",
    maxWidth: "700px",
    margin: "3rem auto",
    boxShadow: "0 4px 15px rgba(0,0,0,0.9)"
  },
  logo: {
    maxWidth: "150px",
    marginBottom: "1rem"
  },
  navSection: {
    marginTop: "2rem",
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "1rem"
  },
  button: {
    padding: "1rem 2rem",
    fontSize: "1rem",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#330055",
    color: "#fff",
    cursor: "pointer"
  }
};

export default TeamDashboard;
